// Fiche d'une créance : règle de calcul, redevable, période, montants, mouvements (émission,
// remise, annulation, relances), imputations des paiements, paiements en attente, quittances (PDF
// et code de vérification) ; encaissement au guichet, remise ou exonération motivée, annulation
// motivée (seulement sans paiement imputé : une créance payée se rembourse d'abord).
import { Bandeau, Bouton, Lien, LienBouton, Panneau, PuceEtat } from '@cc/ui';
import { ArrowLeft, FileDown } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { LIBELLES_CANAL, LIBELLES_ETAT_PAIEMENT, LIBELLES_ETAT_QUITTANCE, LIBELLES_MOUVEMENT, LIBELLES_NATURE_FAIT, francs, libelle, tonPaiement, type QuittanceReponse } from './donnees';
import { FormulaireAction } from './FormulaireAction';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { puceCreance } from './PageCreances';
import { PaiementGuichet } from './PaiementGuichet';
import { telechargerFichier, useCreance, usePaiements, useQuittances, useRubriques } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

export function BoutonPdfQuittance({ q }: { q: QuittanceReponse }) {
  const [erreur, setErreur] = useState<string | null>(null);
  if (!q.pdf_disponible) return <span className={styles.sous}>PDF en cours de production par la tâche de fond</span>;
  return (
    <>
      <Bouton petit variante="secondaire" icone={FileDown} onClick={() => telechargerFichier(`/recettes/quittances/${q.id}/pdf`, `${q.numero}.pdf`).catch((e: unknown) => setErreur(messageErreur(e)))}>
        PDF
      </Bouton>
      {erreur ? <span className={styles.negatif}> {erreur}</span> : null}
    </>
  );
}

export function PageCreance() {
  const { id = '' } = useParams();
  const droits = useDroitsRecettes();
  const fiche = useCreance(id);
  const paiements = usePaiements({ etat: '', canal: '', en_anomalie: '', creance_id: id }, Boolean(id));
  const quittances = useQuittances({ etat: '', creance_id: id }, Boolean(id));
  const rubriques = useRubriques();
  const c = fiche.data;
  const ouverte = c ? c.etat === 'emise' || c.etat === 'partiellement_payee' : false;
  const exonerations = (rubriques.data ?? []).filter((r) => r.categorie === 'exoneration' && r.active && r.rubrique_parent_id === c?.rubrique.id);
  const enAttente = (paiements.data ?? []).filter((p) => p.etat === 'en_attente' || p.etat === 'initie');

  return (
    <>
      <EnTetePage
        etiquette={c ? `Créance, ${libelle(LIBELLES_NATURE_FAIT, c.fait_generateur.nature)}` : 'Créance'}
        titre={c ? `Créance ${c.numero}` : 'Créance'}
        description={c ? <span className={commun.ligne}>{puceCreance(c)}{c.fictif ? <PuceEtat ton="neutre" libelle="Tarif fictif de présentation" /> : null}</span> : null}
        actions={
          <LienBouton href="/recettes/creances" icone={ArrowLeft}>
            Créances
          </LienBouton>
        }
      />
      <NavigationFinances />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L2-A" fonction="La fiche de la créance">
        {c ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              <Panneau titre="Créance">
                <dl className={commun.definitions}>
                  <dt>Libellé</dt>
                  <dd>{c.libelle}</dd>
                  <dt>Redevable</dt>
                  <dd>
                    <Lien href={`/recettes/creances?redevable=${c.redevable.id}`}>{c.redevable.nom}</Lien> ({c.redevable.type === 'entreprise' ? 'entreprise' : 'personne'}{c.redevable.usager_compte_id ? ', compte usager' : ', sans compte usager'})
                  </dd>
                  <dt>Rubrique</dt>
                  <dd>{c.rubrique.libelle} ({c.rubrique.code}) ; {c.rubrique.paiement_partiel_autorise ? 'paiement partiel admis' : 'paiement partiel refusé'}</dd>
                  <dt>Fait générateur</dt>
                  <dd>{libelle(LIBELLES_NATURE_FAIT, c.fait_generateur.nature)}, {c.fait_generateur.objet_type} {c.fait_generateur.objet_id.slice(0, 8)}</dd>
                  <dt>Période</dt>
                  <dd>{c.periode.debut === c.periode.fin ? date(c.periode.debut) : `du ${date(c.periode.debut)} au ${date(c.periode.fin)}`}</dd>
                  <dt>Montant initial</dt>
                  <dd className={styles.mono}>{francs(c.montant_initial)}</dd>
                  {c.montant_remise > 0 ? (
                    <>
                      <dt>Remise</dt>
                      <dd className={styles.mono}>{francs(c.montant_remise)}</dd>
                    </>
                  ) : null}
                  <dt>Montant dû</dt>
                  <dd className={styles.mono}>{francs(c.montant_du)}</dd>
                  <dt>Payé</dt>
                  <dd className={styles.mono}>{francs(c.montant_paye)}</dd>
                  <dt>Reste à payer</dt>
                  <dd className={`${styles.mono} ${styles.montantFort}`} data-testid="reste">{francs(c.reste)}</dd>
                  <dt>Échéance</dt>
                  <dd>
                    {date(c.echeance)}
                    {c.echue ? <strong className={styles.negatif}>, dépassée</strong> : null}
                  </dd>
                  <dt>Émise le</dt>
                  <dd>{dateHeure(c.cree_le)}</dd>
                  {c.motif_annulation ? (
                    <>
                      <dt>Annulation</dt>
                      <dd>{c.motif_annulation} ({dateHeure(c.annulee_le)})</dd>
                    </>
                  ) : null}
                </dl>
                <p className={styles.sous}>Règle de calcul{c.mention ? ` (${c.mention})` : ''}</p>
                <p className={styles.regle} data-testid="regle">{c.regle || 'non renseignée'}</p>
              </Panneau>
              <Panneau titre="Paiements et imputations">
                {c.imputations.length === 0 && enAttente.length === 0 ? <p className={commun.na}>Aucun paiement imputé sur cette créance.</p> : null}
                <ul className={styles.liste}>
                  {enAttente.map((p) => (
                    <li key={p.id}>
                      <span>
                        <Lien href={`/recettes/paiements/${p.id}`} className={styles.mono}>{p.reference_serveur}</Lien>
                        <span className={styles.sous}>{libelle(LIBELLES_CANAL, p.canal)}, initié le {dateHeure(p.initie_le)}</span>
                      </span>
                      <span className={styles.montant}>{francs(p.montant)}</span>
                      <PuceEtat {...tonPaiement(p)} />
                    </li>
                  ))}
                  {c.imputations.map((i) => (
                    <li key={i.id}>
                      <span>
                        <Lien href={`/recettes/paiements/${i.paiement_id}`} className={styles.mono}>{i.reference_serveur}</Lien>
                        <span className={styles.sous}>
                          {libelle(LIBELLES_CANAL, i.canal)}, {dateHeure(i.cree_le)}, paiement {libelle(LIBELLES_ETAT_PAIEMENT, i.paiement_etat).toLowerCase()}
                          {i.quittance_numero ? `, quittance ${i.quittance_numero}` : ''}
                          {i.remboursement_id ? ', remboursement' : i.annulation ? ', annulation' : ''}
                        </span>
                      </span>
                      <span className={`${styles.montant} ${i.montant < 0 ? styles.negatif : ''}`}>{francs(i.montant)}</span>
                    </li>
                  ))}
                </ul>
              </Panneau>
              <Panneau titre="Quittances">
                {quittances.error ? <p className={commun.na}>Quittances non lisibles : {messageErreur(quittances.error)}</p> : null}
                {quittances.data && quittances.data.length === 0 ? <p className={commun.na}>Aucune quittance : une quittance est émise à chaque paiement confirmé.</p> : null}
                <ul className={styles.liste}>
                  {(quittances.data ?? []).map((q) => (
                    <li key={q.id}>
                      <span>
                        <Lien href="/recettes/quittances" className={styles.mono}>{q.numero}</Lien>
                        <span className={styles.sous}>
                          {dateHeure(q.emise_le)} ; code de vérification <span className={styles.codeVerification}>{q.code_verification}</span>
                          {q.mention_environnement ? ` ; ${q.mention_environnement}` : ''}
                        </span>
                      </span>
                      <span className={styles.montant}>{francs(q.montant)}</span>
                      <PuceEtat ton={q.etat === 'valide' ? 'ok' : 'neutre'} libelle={libelle(LIBELLES_ETAT_QUITTANCE, q.etat)} />
                      <BoutonPdfQuittance q={q} />
                    </li>
                  ))}
                </ul>
              </Panneau>
            </div>
            <div className={commun.pile}>
              <Panneau titre="Actions">
                {!droits.creer && !droits.modifier ? <p className={commun.na}>Vous pouvez consulter cette créance ; son traitement est réservé au financier.</p> : null}
                <div className={styles.actions}>
                  {droits.creer ? <PaiementGuichet creance={c} masque={!ouverte} /> : null}
                  {ouverte && droits.modifier ? (
                    <FormulaireAction
                      libelle="Accorder une remise"
                      titre="Remise ou exonération"
                      explication={`Réduit le montant dû (${francs(c.montant_du)}), jamais sous le payé (${francs(c.montant_paye)}). Au-delà du seuil (20 % par défaut), une anomalie est ouverte. Une exonération s'appuie sur une rubrique liée active.`}
                      chemin={`/recettes/creances/${c.id}/remise`}
                      champs={[
                        { nom: 'montant', libelle: 'Montant de la remise (F)', type: 'nombre', obligatoire: true },
                        { nom: 'motif', libelle: 'Motif', type: 'multiligne', obligatoire: true },
                        ...(exonerations.length > 0 ? [{ nom: 'rubrique_exoneration_id', libelle: 'Exonération (rubrique liée)', type: 'selection' as const, options: exonerations.map((r) => ({ valeur: r.id, libelle: r.libelle })) }] : []),
                        { nom: 'piece_document_id', libelle: 'Identifiant de la pièce justificative', aide: 'Document déjà déposé, facultatif' },
                      ]}
                      succes="Remise enregistrée."
                      variante="secondaire"
                    />
                  ) : null}
                  {ouverte && droits.modifier ? (
                    <FormulaireAction
                      libelle="Annuler la créance"
                      titre="Annuler la créance"
                      explication={c.montant_paye > 0 ? 'Cette créance porte un paiement imputé : le serveur refusera l\'annulation. Proposez d\'abord un remboursement depuis la fiche du paiement.' : 'La créance reste visible en état annulée avec son motif ; sans pièce justificative, une anomalie est ouverte.'}
                      chemin={`/recettes/creances/${c.id}/annuler`}
                      champs={[
                        { nom: 'motif', libelle: 'Motif', type: 'multiligne', obligatoire: true },
                        { nom: 'piece_document_id', libelle: 'Identifiant de la pièce justificative', aide: 'Document déjà déposé, facultatif' },
                      ]}
                      succes="Créance annulée."
                      variante="danger"
                    />
                  ) : null}
                </div>
                {c.etat === 'annulee' ? <Bandeau type="info">Créance annulée ; elle reste consultable.</Bandeau> : null}
                {c.etat === 'soldee' ? <Bandeau type="succes">Créance soldée.</Bandeau> : null}
              </Panneau>
              <Panneau titre="Mouvements">
                <ol className={styles.liste}>
                  {c.mouvements.map((m, i) => (
                    <li key={i}>
                      <span>
                        <strong>{libelle(LIBELLES_MOUVEMENT, m.type)}</strong>
                        <span className={styles.sous}>
                          {dateHeure(m.horodatage)}
                          {m.motif ? ` ; motif : ${m.motif}` : ''}
                          {m.piece_document_id ? ' ; pièce jointe' : ''}
                          {typeof m.donnees?.rang === 'number' ? ` ; relance ${String(m.donnees.rang)}` : ''}
                        </span>
                      </span>
                      <span className={`${styles.montant} ${m.montant < 0 ? styles.negatif : ''}`}>{francs(m.montant)}</span>
                    </li>
                  ))}
                </ol>
              </Panneau>
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
